import supabaseClient from '@/api/supabaseClient.ts';
import useLogin from '@/api/useLogin.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import { rotateSession } from '@/utils/trackingSession';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';

interface UserWithRole {
  id: string;
  role: 'owner' | 'goer' | 'admin' | 'trainer';
  email: string;
  email_verified: boolean;
}

interface AuthContextProps {
  session: Session | null;
  user: UserWithRole | null;
  isAdmin: boolean;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  gymId: string | null;
  setGymId: (gymId: string | null) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [gymId, setGymId] = useState<string | null>(null);

  const logout = useCallback(async () => {
    const { error } = await supabaseClient.auth.signOut();
    if (error) {
      console.error('Logout failed:', error.message);
    }
    rotateSession();
    setUser(null);
    setSession(null);
  }, []);

  const loginQuery = useLogin();

  const sendResetPasswordLinkQuery = useSendResetPasswordLink();

  const resetPasswordQuery = useResetPassword();

  const verifyEmailQuery = useVerifyEmail();

  const resendVerificationCodeQuery = useResendVerificationCode();

  const isAdmin = user?.role === 'admin' && (user?.email?.endsWith('@gympera.com') ?? false);

  const checkData = useCallback(async (session: Session | null, forceRefresh = false) => {
    // Prevent concurrent checks
    if (isPendingAuthChangeCheck.current && !forceRefresh) return;

    isPendingAuthChangeCheck.current = true;

    try {
      if (!session) {
        setSession(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Pobierz profil z bazy
      const { data: profile, error } = await supabaseClient
        .from('profiles')
        .select('id, role, email, email_verified')
        .eq('id', session.user.id)
        .maybeSingle();

      if (error) {
        // Ignoruj błąd "not found" - może się zdarzyć w trakcie rejestracji
        if (error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }

        // Jeśli profil nie istnieje ale mamy sesję - użytkownik jest w trakcie OAuth
        // lub profil jeszcze się nie stworzył (race condition)
        setUser(null);
        setSession(session);
        setIsLoading(false);
        return;
      }

      if (profile) {
        setUser({
          id: profile.id,
          role: profile.role,
          email: profile.email || session.user.email || '',
          email_verified: profile.email_verified,
        });
        setSession(session);
      } else {
        // Profil nie istnieje - może być OAuth w trakcie
        setUser(null);
        setSession(session);
      }
    } catch (err) {
      console.error('Unexpected error in checkData:', err);
      setUser(null);
      setSession(null);
    } finally {
      setIsLoading(false);
      isPendingAuthChangeCheck.current = false;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await supabaseClient.auth.getSession();
    if (data.session) {
      await checkData(data.session, true);
    }
  }, [checkData]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setSession(null);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isAdmin,
        supabaseClient,
        logout,
        isLoading,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
        gymId,
        setGymId,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}
